import { Button, DrawerFooter } from "@/components";
import {
  FormDatePicker,
  FormDropzoneMultiple,
  FormInput,
  FormSelect,
  FormSelectMultiple,
} from "@/components/Form";
import { DROPZONE_ACCEPT } from "@/constants/constants";
import { ROUTES } from "@/constants/routes";
import { useCreateChat, useGetListChatMember } from "@/hooks";
import { ECHAT_THEME, ECHAT_THEME_LABEL, ECHAT_THEME_TYPE, NewChatParams } from "@/types";
import { formatDateCustom } from "@/utils";
import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  RoomInformationFormData,
  roomInformationResolver,
} from "../../schemas/RoomInfomationSchema";

interface Props {
  onClose: () => void;
}

const FormCreate: React.FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();

  // search input for cc select
  const [searchInput, setSearchInput] = useState<string>("");

  const { control, handleSubmit } = useForm({
    resolver: roomInformationResolver,
    defaultValues: {
      start_date: formatDateCustom(new Date()),
      end_date: formatDateCustom(new Date()),
    },
  });

  // query to create new chat room
  const { createChat, isCreating } = useCreateChat();

  // parse form data to data for update chat API
  const parseFormDataToUpdateChatParams = useCallback(
    (data: RoomInformationFormData): NewChatParams => {
      return {
        title: data.title,
        start_date: formatDateCustom(data.start_date),
        end_date: formatDateCustom(data.end_date),
        theme_type: data.theme_type.value as ECHAT_THEME_TYPE,
        theme: data.theme.value as ECHAT_THEME,
        description: data.description,
        image: data.image.pop()?.file || data.image.pop()?.url || undefined, // only support upload one image
        viewer_ids:
          data.viewer_ids?.map((viewer) => viewer.value as number) || [],
      };
    },
    []
  );

  const saveRoomInformation = (data: RoomInformationFormData) => {
    // TODO: Implement save functionality
    const params = parseFormDataToUpdateChatParams(data);
    createChat(params).then((data) => {
      onClose();
      navigate(ROUTES.CHAT + `/${data.data.id}`);
    });
  };

  // Load options for select
  // theme type options
  const themeTypeOptions = useMemo(() => {
    return Object.entries(ECHAT_THEME_TYPE).map(([_, value]) => ({
      label: ECHAT_THEME_LABEL[value],
      value: value,
    }));
  }, []);

  // theme options
  const themeOptions = useMemo(() => {
    return Object.entries(ECHAT_THEME).map(([_, value]) => ({
      label: value,
      value: value,
    }));
  }, []);

  // Get list cc to select
  const {
    members,
    isLoading: isLoadingViewers,
    fetchNextPage: fetchNextPageViewers,
    hasNextPage: hasNextPageViewers,
  } = useGetListChatMember(0, { search: searchInput, s: searchInput }); // room id = 0 for create new room

  const optionMembers = useMemo(() => {
    return members.map((member) => ({
      label: member.user_name,
      value: member.user_id,
      description: member.user_email,
    }));
  }, [members]);

  return (
    <div className="flex flex-col h-full">
      <form
        className="flex-1 p-4 space-y-4 overflow-auto"
        onSubmit={handleSubmit(saveRoomInformation)}
      >
        <FormInput
          label="タイトル"
          control={control}
          name="title"
          required
          maxLength={150}
        />

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-2 gap-y-4">
          <FormDatePicker
            control={control}
            name="start_date"
            label="相談日"
            required
            popperPlacement="bottom-start"
          />

          <FormDatePicker
            control={control}
            name="end_date"
            label="解答納期"
            required
          />

          <FormSelect
            control={control}
            name="theme_type"
            options={themeTypeOptions}
            label="相談者の属性"
            required
          />

          <FormSelect
            control={control}
            name="theme"
            options={themeOptions}
            label="テーマ"
            required
          />
        </div>

        <FormSelectMultiple
          control={control}
          name="viewer_ids"
          options={optionMembers}
          label="CC"
          isLoadingMoreItems={isLoadingViewers}
          onLoadMoreItems={fetchNextPageViewers}
          stopCheckScrollToBottom={!hasNextPageViewers}
          enableOptionDescription
          placeholder="CCを選択する"
          allowSearchInput
          onSearchInputChange={setSearchInput}
        />

        <FormInput
          label="相談内容"
          control={control}
          name="description"
          required
          multiline
          rows={6}
          maxLength={1000}
        />

        <FormDropzoneMultiple
          control={control}
          name="image"
          accept={{
            ...DROPZONE_ACCEPT.IMAGE.COMMON,
            ...DROPZONE_ACCEPT.PDF,
          }}
          maxSizeInBytes={25 * 1024 * 1024} // 25MB
          errorFileSize="最大アップロードサイズを超過しています。サイズの上限は25MBです。"
          errorInvalidType="画像は PNG, JPG, JPGE, PDF のうちいずれかの形式のファイルを指定してください。"
          content={
            <div className="text-center text-sm space-y-2">
              <div className="text-light_primary">
                ファイルを選択するか、ここにドラッグ＆ドロップしてください。
              </div>
              <div className="text-light_secondary text-xs">
                推奨ファイル形式: PNG, JPG, JPGE, PDF
              </div>
              <div className="text-light_secondary text-xs">
                動画/ワード/エクセルなどは、初回ルーム作成後に送付可能です。
              </div>
              <div className="underline text-xs text-cyan">アップロード</div>
            </div>
          }
          maxFiles={1}
          syncMode="replace"
        />
      </form>

      <DrawerFooter>
        <Button
          type="button"
          buttonType="text"
          color="default"
          className="!h-12"
          onClick={onClose}
        >
          キャンセル
        </Button>

        <Button
          type="button"
          variant="solid"
          color="primary"
          className="!h-12"
          onClick={handleSubmit(saveRoomInformation)}
          isLoading={isCreating}
        >
          作成する
        </Button>
      </DrawerFooter>
    </div>
  );
};

export default FormCreate;
